/*
 * Правая группа шапки. В base.css её нет: там шапка фазы 1, где справа стояло одно имя.
 *
 * ГЛАВНОЕ ЗДЕСЬ — ПОВЕДЕНИЕ НА УЗКОМ ОКНЕ. Шапка — единственный ряд, который обязан
 * помещаться всегда: горизонтальный скролл страницы запрещён (base.css: `overflow-x: clip`
 * на html и body), и «не помещается» означало бы не полосу прокрутки, а обрезанный орган
 * управления публикациями. Поэтому подписи гаснут по порядку убывания важности —
 * сперва имя человека (лицо остаётся кружком), потом подпись кнопки (остаётся значок),
 * потом вордмарк (остаётся знак). Ни один орган при этом не исчезает.
 *
 * ЛЕСТНИЦА 1200/1060/900/720 — НЕ БРЕЙКПОИНТЫ РАСКЛАДКИ, а порядок, в котором эти подписи
 * гаснут; её числа зеркалит `RunStatusBar.module.css`. Сводить её на общую шкалу адаптива
 * (640/1024/1280/1800, docs/ui-design.md §4.1) НЕЛЬЗЯ: подписи начнут исчезать все разом на
 * ноутбуке в 1000px. Шаг 640 к лестнице ДОБАВЛЕН и делает то, чего лестница не умела, —
 * заставляет шапку сжиматься, а не обрезаться (см. её блок внизу файла).
 */

/*
 * Красная нижняя граница шапки на паузе — периферийный признак того же состояния, что и
 * переключатель: одобряя пост, человек смотрит на пост, а не на шапку, и «публикации
 * выключены» обязано доходить боковым зрением.
 *
 * Селектор составной (`:global(.hdr).paused`), а не одиночный: base.css задаёт `border-bottom`
 * сокращённой записью и подключается ПОСЛЕ модулей компонентов (main.tsx импортирует App
 * раньше стилей). При равной специфичности выиграл бы он, и правило не сработало бы вовсе.
 */
:global(.hdr).paused {
  border-bottom-color: var(--red);
}

/*
 * ══ полоса приложения ═══════════════════════════════════════════════════════
 *
 * До 08.09.2026 полоса не была ограничена ничем, и на 4K это давало не «растянутый текст», а
 * РАЗРЫВЫ: список бэклога прибит к левому краю окна, пост отцентрован в остатке (у панели
 * свой `max-width`), между ними ~1030px пустоты, а шапка идёт на всю ширину поверх обоих.
 * `--shell-max` (1800px = 380 список + 1400 панель + запас) ставит потолок один раз и на
 * оба ряда, поэтому шапка и содержимое стоят в одной колонке. Ниже 1800px не меняется ничто.
 *
 * ШАПКА ОГРАНИЧЕНА ВНУТРИ, А НЕ СНАРУЖИ. `.hdr` остаётся полосой во всю ширину окна: его
 * нижняя граница — периферийный признак остановленных публикаций, и оборванная посередине
 * монитора красная чёрточка этим признаком быть перестаёт. Поэтому `gap`/`padding`/`flex`
 * сняты с `.hdr` и переехали на `.hdrInner`.
 *
 * Селекторы `.hdr` составные (`:global(.hdr).frame`) по той же причине, что и `.paused`:
 * base.css подключается ПОСЛЕ модулей, и при равной специфичности выиграл бы он.
 */
:global(.hdr).frame {
  gap: 0;
  padding: 0;
}

.hdrInner {
  width: 100%;
  max-width: var(--shell-max);
  margin-inline: auto;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 28px;
  padding: 0 24px;
  /* Флекс-элементы шапки не имеют права раздвинуть её шире окна: горизонтального скролла у
     страницы нет (`overflow-x: clip`), и «не поместилось» означало бы обрезанный орган. */
  min-width: 0;
}

/* Содержимое под шапкой. Ряд, в колонку — только на телефоне (см. ниже). */
.body {
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: var(--shell-max);
  margin-inline: auto;
}

.tools {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
  min-width: 0;
}

/* Граница между «состоянием системы» и «моими действиями»: два фиолетовых пятна подряд
   иначе читаются одним органом. */
.sep {
  width: 1px;
  height: 24px;
  background: var(--border);
  flex: none;
}

.run {
  flex: none;
}

.profile {
  background: none;
  border: none;
  padding: 2px 4px;
  border-radius: var(--r-pill);
  font-family: var(--sans);
  cursor: pointer;
  max-width: 220px;
}
.profile:hover {
  background: var(--bg-alt);
}

.uname {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Вкладки не сжимаются и не переносятся: «Контент-план» в две строки внутри полосы 56px —
   это уже не вкладка. Ширину отдают надписи справа, а не навигация — до 640px включительно,
   где отдавать уже нечего и вкладки становятся прокручиваемой лентой (см. блок ≤640 внизу). */
.tabs {
  flex: none;
  white-space: nowrap;
}

/* ── сжатие ────────────────────────────────────────────────────────────────── */
/*
 * ЛЕСТНИЦА ПЕРЕМЕРЕНА 19.09.2026 (docs/release-2026-09-19.md §1) — и не на глаз, а линейкой:
 * снимки шапки в Chromium с настоящим Geist на 1920…390 и ИДУЩИМ прогоном в полосе хода. Без
 * прогона шапка у́же на 232px, и прежняя лестница проверялась именно так — поэтому с прогоном
 * она обрезала орган уже до этой правки: на 1280 не хватало 117px, на 720–1060 — 9–60px.
 * Кнопка «Прогон мониторинга» (186px с подписью) сделала обрез видимым на любом ноутбуке.
 *
 * Полная ширина органов с прогоном — 1593px (1773 с длинным именем человека). Ступени ниже
 * сняты так, чтобы на каждой оставался запас не меньше 17px при самом широком содержимом:
 *
 *   1799  имя человека                       (лицо остаётся кружком)
 *   1599  подпись «Настройки», длинная подпись главной кнопки → «Генерация», полоса хода 196
 *   1439  подпись «Прогон мониторинга»       (остаётся значок-радар)
 *   1279  вордмарк, полоса хода 148
 *   1099  воздух между органами
 *   1023  вкладки становятся прокручиваемой лентой, главная кнопка — значком, полоса 104
 *    899  полоса хода 56 — дорожка и точка
 *    640  шапка в две строки: органы сверху, лента вкладок — второй строкой во всю ширину
 *
 * Это по-прежнему НЕ брейкпоинты раскладки (docs/ui-design.md §4.1), а порядок, в котором гаснут
 * подписи органов; числа повторены в `WriterModelSwitch.module.css`, `RunStatusBar.module.css`
 * и `MonitoringRunButton.module.css`. Ни один орган не исчезает ни на одной ширине: кнопки,
 * переключатель и профиль остаются нажимаемыми, тексты держат `aria-label` и подсказки.
 */

@media (max-width: 1799px) {
  .uname {
    display: none;
  }
}

/* Короткая подпись прячется, пока помещается длинная: видимая надпись ровно одна. */
.runShort {
  display: none;
}

@media (max-width: 1599px) {
  .runLong {
    display: none;
  }
  .runShort {
    display: inline;
  }
}

@media (max-width: 1279px) {
  .wordmark {
    display: none;
  }
}

@media (max-width: 1099px) {
  .tools {
    gap: 6px;
  }
  .hdrInner {
    gap: 14px;
    padding: 0 14px;
  }
}

/*
 * 1023 (планшет): ширину отдаёт НАВИГАЦИЯ. Прежде лента вкладок сжималась только на телефоне, а
 * между 641 и 1023 вкладки стояли `flex: none` — и шапка с идущим прогоном отрезала профиль.
 * Лента прокручивается; обрезанная на краю вкладка показывает, что дальше есть ещё.
 * Главная кнопка остаётся значком «+»: имя держит aria-label, а «+» рядом с профилем читается
 * как «создать» без подписи.
 */
@media (max-width: 1023px) {
  .hdrInner :global(.tabs) {
    flex: 1 1 auto;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .hdrInner :global(.tabs)::-webkit-scrollbar {
    display: none;
  }
  .runShort {
    display: none;
  }
  .run {
    padding: 11px 13px;
  }
}

/* ── телефон (640) ───────────────────────────────────────────────────────────
 *
 * ШАПКА В ДВЕ СТРОКИ. На 390px органы управления (рубильник, настройки, ход прогона, прогон
 * мониторинга, генерация, профиль) занимают ≈350px, и в одну строку с ними вкладкам оставалось
 * 0–11px: навигация физически существовала, но была недостижима. Жертвовать органом нельзя
 * (правило шапки), поэтому вкладки уходят ВТОРОЙ СТРОКОЙ во всю ширину. Порядок в DOM не
 * меняется — только `order` и перенос, — поэтому `Shell.test.tsx`, закрепивший порядок
 * органов, остаётся верным, а скринридер читает шапку как прежде.
 *
 * Высоту шапки отпускаем (`height: auto`): от 56px в раскладке не зависит ничего, кроме самой
 * полосы — проверено грепом по стилям пакета 19.09.2026.
 *
 * Шеврон профиля — чистая аффорданса «здесь раскроется»: кружок с инициалами и так читается
 * как вход в аккаунт.
 *
 * Селекторы составные (`.hdrInner :global(.tabs)`, 0,2,0) — иначе одиночный глобальный `.tabs`
 * из base.css, подключённого позже, перебил бы модульный.
 */
@media (max-width: 640px) {
  :global(.hdr).frame {
    height: auto;
  }
  .hdrInner {
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 0;
    padding: 0 10px;
  }
  .hdrInner :global(.brand) {
    height: 52px;
  }
  .hdrInner :global(.hdr-sp) {
    display: none;
  }
  .tools {
    order: 2;
    height: 52px;
    margin-left: auto;
    gap: 4px;
  }
  .hdrInner :global(.tabs) {
    order: 3;
    flex: 1 0 100%;
    height: 42px;
  }
  .hdrInner :global(.tab) {
    padding: 0 12px;
    font-size: 13px;
  }
  .profile svg {
    display: none;
  }
  /* Список бэклога — во всю ширину, пост под ним (ширину задаёт `.aside` в base.css).
     Полоса в 156px, которую давал `max-width: 40vw` на 390px, не список и не пост. */
  .body {
    flex-direction: column;
  }
}
